import type { ReactNode } from 'react'
import type { LayoutChangeEvent, ViewStyle } from 'react-native'
import { useRouter } from 'expo-router'
import { BlurView } from 'expo-blur'
import { LinearGradient } from 'expo-linear-gradient'
import { observer } from 'mobx-react-lite'
import React, { useRef, useState, RefObject } from 'react'
import { Image, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native'
import { Divider } from 'react-native-paper'
import Animated, { useAnimatedStyle, useDerivedValue, useSharedValue, withTiming } from 'react-native-reanimated'

import { formatNumber, formatNumberUS } from '@ppwr/poker'

import { DS } from '../../design-system'
import { useStore } from '../../models'
import { mapPosition } from '../../utils/positionMapping'
import {
  mapPlayingStyleDescription,
  mapPlayingStyleIcon,
  parseDateStart
} from '../game-statistics-screen/GameStatsUtils'
import arrowDown from './assets/Caret-down.png'
import arrowUp from './assets/Caret-up.png'
import CircularGradientProgress from './CircularGradient'

export enum AccordianType {
  gameType = 'GameType',
  upcomingEvent = 'UpcomingEvent'
}

function AccordionItem({
  isExpanded,
  children,
  viewKey,
  style = {},
  duration = 500
}: {
  isExpanded: boolean
  children: ReactNode
  viewKey: string
  style?: ViewStyle
  duration?: number
}) {
  const height = useSharedValue(0)

  const derivedHeight = useDerivedValue(() =>
    withTiming(height.value * Number(isExpanded), {
      duration
    })
  )
  const bodyStyle = useAnimatedStyle(() => ({
    height: derivedHeight.value
  }))

  return (
    <Animated.View key={`accordionItem_${viewKey}`} style={[styles.animatedView, bodyStyle, style]}>
      <View
        onLayout={(e) => {
          height.value = e.nativeEvent.layout.height
        }}
        style={styles.wrapper}
      >
        {children}
      </View>
    </Animated.View>
  )
}

const GameHistory = observer(() => {
  const router = useRouter()
  const {
    game: { myGameStats, myPlayerGameStats }
  } = useStore()

  return (
    <DS.Frame style={{}}>
      <DS.Frame gap="8" row style={{ alignItems: 'center', paddingVertical: 15 }}>
        <Image source={require('./assets/Stars.png')} style={styles.starIcon} />
        <DS.Text variant="Body Medium/Regular">
          {`${myGameStats.gameType} • ${parseDateStart(myGameStats.gameStartTime)}`}
        </DS.Text>
      </DS.Frame>
      <DS.Frame style={{ flex: 1, flexDirection: 'row', justifyContent: 'space-between' }}>
        <DS.Frame bg="Global/White" style={styles.recentGameRankContainer}>
          <LinearGradient
            // Button Linear Gradient
            colors={['#E9E238', '#FF7222']}
            start={{ x: 0, y: 0.75 }}
            end={{ x: 1, y: 0.25 }}
            style={styles.rankTextGradientContainer}
          >
            <View style={styles.rankTextContainer}>
              <DS.Text variant="Heading 1/Medium">{`#${myPlayerGameStats?.rank ?? '-'}`}</DS.Text>
            </View>
          </LinearGradient>
          <DS.Text variant="Caption/Medium" style={{ marginTop: 8 }}>
            GAME RANK
          </DS.Text>
          <DS.Text variant="Caption/Regular" style={{ color: '#68706D', marginTop: 8 }}>
            You were the{mapPosition(myPlayerGameStats?.rank ?? 0)}best player in this game.
          </DS.Text>
        </DS.Frame>
        <DS.Frame bg="Global/White" style={styles.potWinContainer}>
          <DS.Frame row style={{ justifyContent: 'flex-start', alignItems: 'center', paddingVertical: 2 }}>
            <DS.Text variant="Heading 1/Medium">{formatNumber(myPlayerGameStats?.biggestWin?.chipsWon ?? 0)}</DS.Text>
            <Image source={require('./assets/Chip-filled.png')} style={styles.chipsFilledIcon} />
          </DS.Frame>
          <DS.Text variant="Caption/Medium" style={{ marginTop: 8 }}>
            BIGGEST POT WON
          </DS.Text>
          <DS.Text variant="Caption/Regular" style={{ color: '#68706D', marginTop: 8 }}>
            This was your biggest pot won during the game.
          </DS.Text>
        </DS.Frame>
      </DS.Frame>
      <DS.Frame bg="Global/White" style={styles.gameEffectivenessContainer}>
        <View style={{ flexDirection: 'row' }}>
          <View style={{ flex: 1 }}>
            <CircularGradientProgress
              size={100} // Diameter of the circular progress bar
              strokeWidth={10} // Thickness of the progress stroke
              progress={myGameStats.percentage.handsWon ?? 0} // Progress value (0 - 100)
              colors={['#FF3ED4', '#480D93']} // Gradient colors
            />
          </View>
          <View style={styles.winRateContainer}>
            <DS.Text variant="Caption/Medium">WIN RATE EFFICIENCY</DS.Text>
            <DS.Text variant="Caption/Medium" style={{ color: '#68706D', marginTop: 8 }}>
              {`This is the percentage of hands you won (${
                formatNumberUS(myGameStats.percentage.handsWon) ?? '-'
              }%) out of the hands played (${formatNumberUS(myGameStats.percentage.handsPlayed) ?? '-'}%).`}
            </DS.Text>
          </View>
        </View>
        <Divider style={{ marginTop: 10 }} />
        <DS.Frame gap="8">
          <Image source={require('./assets/down-traingle.png')} style={styles.downTraingleIcon} />
          <DS.Text variant="Body Small/Regular" textAlign="center">
            This measures your effectiveness in the game. A higher efficiency means better decision-making and strategy.
          </DS.Text>
        </DS.Frame>
      </DS.Frame>
      <DS.Frame bg="Global/White" style={styles.playingStyleContainer}>
        <View style={{ flexDirection: 'row', paddingVertical: 10 }}>
          <View style={styles.preflopFoldContainer}>
            <CircularGradientProgress
              size={100} // Diameter of the circular progress bar
              strokeWidth={10} // Thickness of the progress stroke
              progress={myGameStats.percentage.preFlopFold} // Progress value (0 - 100)
              colors={['#E9E238', '#FF7222']} // Gradient colors
            />
            <DS.Text variant="Caption/Medium" textAlign="center" style={{ paddingTop: 5 }}>
              PRE-FLOP FOLDS
            </DS.Text>
          </View>
          <View style={styles.versusContainer}>
            <DS.Text variant="Caption/Medium">VS</DS.Text>
          </View>
          <View style={styles.showdownWinContainer}>
            <CircularGradientProgress
              size={100} // Diameter of the circular progress bar
              strokeWidth={10} // Thickness of the progress stroke
              progress={myGameStats.percentage.winsAtShowdown ?? 0} // Progress value (0 - 100)
              colors={['#5BC4FF', '#480D93']} // Gradient colors
            />
            <DS.Text variant="Caption/Medium" textAlign="center" style={{ paddingTop: 5 }}>
              WINS AT SHOWDOWN
            </DS.Text>
          </View>
        </View>
        <DS.Text variant="Caption/Regular" textAlign="center" style={{ color: '#68706D' }}>
          Pre-flop folds show how often you fold before the flop. Wins at showdown show how often you win when cards are
          revealed. Together, they reveal your play style.
        </DS.Text>
        <Divider style={{ marginTop: 10 }} />
        <DS.Frame gap="8">
          <Image source={require('./assets/down-traingle.png')} style={styles.downTraingleIcon} />
          <DS.Text textAlign="center" variant="Caption/Medium">
            YOU PLAYED
          </DS.Text>
          <DS.Text textAlign="center" variant="Heading 4/Medium">
            {myGameStats.playingStyle} Style
          </DS.Text>
          <DS.Text textAlign="center" variant="Body Small/Regular">
            {mapPlayingStyleDescription(myGameStats.playingStyle)}
          </DS.Text>
          <DS.Frame style={styles.turtleFrame}>
            {mapPlayingStyleIcon(myGameStats.playingStyle, 'Brand/Maroon')}
          </DS.Frame>
        </DS.Frame>
      </DS.Frame>
      <DS.Button
        variant={'Large/Primary'}
        type={'On Light'}
        onPress={async () => {
          router.navigate('/game/new')
        }}
        style={styles.practiceGameButton}
      >
        Play practice game
      </DS.Button>
      <DS.Button
        variant={'Large/Primary'}
        type={'On Dark'}
        onPress={async () => {
          router.navigate('/profile')
        }}
        style={styles.seeAllTimeStatsButton}
      >
        See all-time stats
      </DS.Button>
    </DS.Frame>
  )
})

const CalendarDay = ({ month, date }: { month: string; date: number }) => {
  return (
    <DS.Frame style={styles.calendarContainer}>
      <DS.Frame alignItems="center" justifyContent="center" bg="Brand/Maroon" style={styles.calendarFrame}>
        <DS.Text variant="Caption/Bold" color="Global/White" textAlign="center">
          {month}
        </DS.Text>
      </DS.Frame>
      <DS.Frame flex={1} justifyContent="center">
        <DS.Text variant="Heading 3/Medium" textAlign="center">
          {date}
        </DS.Text>
      </DS.Frame>
    </DS.Frame>
  )
}

function UpcomingEvent() {
  const getTodaysDate = () => {
    const today = new Date()
    return today.getDate()
  }

  const [selectedDate, setSelectedDate] = useState(getTodaysDate())
  const [avatars, setAvatars] = useState([
    { id: 1, uri: 'https://maineventtravel.com/media/news/MPF/AJRPokerPower.jpg' },
    { id: 2, uri: 'https://pokerpower.com/wp-content/uploads/2023/11/Abby_Merk.jpg' },
    { id: 3, uri: 'https://pokerpower.com/wp-content/uploads/2024/02/Betina-scaled-e1707839410924.jpg' }
  ])
  const isSubscribed = true
  const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
  const getNextFiveDates = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = today.getMonth() // 0-based index for months

    const nextDates = []

    // Start from tomorrow
    for (let i = 0; i <= 5; i++) {
      const nextDate = new Date(year, month, today.getDate() + i)

      // Make sure we stay within the current month
      if (nextDate.getMonth() === month) {
        // console.log('nextDate: ', nextDate.getDate())
        nextDates.push(nextDate.getDate())
      } else {
        break // Stop if we've moved to the next month
      }
    }

    return nextDates
  }

  const getCurrentMonth = () => {
    const todaysDate = new Date()
    return MONTHS[todaysDate.getMonth()]
  }

  const nextFiveDates = getNextFiveDates()
  // nextFiveDates.forEach((date) => console.log(date.toDateString()))

  return (
    <DS.Frame style={{ width: '100%' }}>
      {isSubscribed ? (
        <DS.Frame>
          <ScrollView
            style={{ padding: 5 }}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ width: '100%', justifyContent: 'center' }}
          >
            {/* <View style={styles.upcomingContainer}> */}
            {avatars.map((avatar) => (
              <View key={avatar.id} style={styles.avatarContainer}>
                <Image source={{ uri: avatar.uri }} style={styles.avatar} />
              </View>
            ))}
            {/* </View> */}
          </ScrollView>
          <DS.Text variant="Body Medium/Regular" textAlign="center">
            Get a 1:1 private lesson or join a Teaching Table to get expert coaching.
          </DS.Text>
          <DS.Button
            variant={'Large/Primary'}
            type={'On Dark'}
            onPress={async () => {
              console.log('Submit Rating')
            }}
            style={styles.subscribeButton}
          >
            Subscribe
          </DS.Button>
        </DS.Frame>
      ) : (
        <View style={{ width: '100%', paddingVertical: 10 }}>
          <View style={{ flexDirection: 'row' }}>
            <View style={{ flex: 1, justifyContent: 'center' }}>
              <DS.Text variant="Body Small/Regular">{getCurrentMonth()}</DS.Text>
            </View>
            <View style={styles.upcomingDateContainer}>
              {nextFiveDates.map((date, i) => (
                <TouchableOpacity
                  key={`upcoming-date-${date.toString()}-${i}`}
                  style={[
                    styles.touchableDateCOntainer,
                    {
                      backgroundColor: selectedDate === date ? 'rgba(123, 4, 61, 0.15)' : 'transparent'
                    }
                  ]}
                  onPress={() => setSelectedDate(date)}
                >
                  {selectedDate === date && <View style={styles.selectedDate} />}
                  <DS.Text variant="Body Small/Regular">{date}</DS.Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <DS.Frame bg="Global/White" style={styles.upcomingEventContainer}>
            <View style={{ flexDirection: 'row' }}>
              <View style={styles.calendarAndDetailContainer}>
                <CalendarDay month={getCurrentMonth()} date={selectedDate} />
                <DS.Frame gap="8" style={{ paddingLeft: 10 }}>
                  <DS.Text variant="Body Small/Regular">Private lesson</DS.Text>
                  <DS.Text variant="Body Medium/Medium">Tammi Endemann</DS.Text>
                  <DS.Text variant="Body Small/Regular">{'Today  3 PM PST'}</DS.Text>
                </DS.Frame>
              </View>
              <View style={{ flex: 1 }}>
                <Image
                  source={{ uri: 'https://maineventtravel.com/media/news/MPF/AJRPokerPower.jpg' }}
                  style={styles.avatar}
                />
              </View>
            </View>
          </DS.Frame>
        </View>
      )}
    </DS.Frame>
  )
}

function Parent({ open, accrodianType }: { open: boolean; accrodianType: AccordianType }) {
  return (
    <View style={{ width: '100%' }}>
      <AccordionItem isExpanded={open} viewKey="Accordion">
        {accrodianType === AccordianType.gameType ? <GameHistory /> : <UpcomingEvent />}
      </AccordionItem>
    </View>
  )
}

export const Accordian = ({
  title,
  accordianType,
  scrollViewRef
}: {
  title: string
  accordianType: AccordianType
  scrollViewRef: RefObject<ScrollView | null>
}) => {
  // const open = useSharedValue(false)
  const [isAccordianOpened, setIsAccordianOpened] = useState(true)
  const itemRef = useRef<View>(null)

  const [layout, setLayout] = useState({ y: 0, height: 0 })
  const handleLayout = (event: LayoutChangeEvent) => {
    const { y, height } = event.nativeEvent.layout
    setLayout({ y, height })
  }

  const onPress = () => {
    // open.value = !open.value
    setIsAccordianOpened(!isAccordianOpened)
    if (!isAccordianOpened && scrollViewRef.current) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: layout.y, animated: true })
      }, 500) // Delay ensures content is fully expanded before scrolling
    }
  }
  return (
    <View ref={itemRef} onLayout={handleLayout} style={[{ marginBottom: 10 }, containerStyles.webWrapper]}>
      <BlurView
        // blurType="light"
        intensity={70}
        tint="light"
        style={[
          {
            backgroundColor: isAccordianOpened ? 'transparent' : 'rgba(255, 255, 255, 0.50)'
          },
          styles.accordianMainContainer
        ]}
      >
        <TouchableOpacity onPress={onPress} style={[styles.titleContainer]}>
          <DS.Text variant="Heading 3/Medium">{title}</DS.Text>
          <Image source={isAccordianOpened ? arrowUp : arrowDown} style={styles.topDownCaretIcon} />
        </TouchableOpacity>

        <DS.Frame style={styles.content}>
          <Parent open={isAccordianOpened} accrodianType={accordianType} />
          {/* <Parent open={open} /> */}
        </DS.Frame>
      </BlurView>
    </View>
  )
}

const containerStyles = StyleSheet.create({
  webWrapper: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center'
  }
})

const styles = StyleSheet.create({
  container: {
    flex: 1
    // justifyContent: 'center',
    // paddingTop: 24
  },
  titleContainer: {
    // paddingBottom: 16,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  parent: {
    width: 200
  },
  wrapper: {
    width: '100%',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center'
  },
  animatedView: {
    width: '100%',
    overflow: 'hidden'
  },
  box: {
    height: 120,
    width: 120,
    color: '#f8f9ff',
    backgroundColor: '#b58df1',
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center'
  },
  upcomingContainer: {
    flex: 1,
    flexDirection: 'row',
    paddingVertical: 10,
    backgroundColor: 'red'
    // paddingHorizontal: 10
  },
  avatarContainer: {
    borderWidth: 2, // Slightly thicker border for emphasis
    borderColor: '#fff', // White border around the avatar
    borderRadius: 50, // To ensure the image stays circular
    padding: 3,
    marginRight: -20, // Slight overlap between avatars
    backgroundColor: '#f0f0f0' // Background to create the circular effect
  },
  avatar: {
    width: 50, // Adjust the size to match the example
    height: 50,
    borderRadius: 25 // Circular shape
  },
  monthDatecontainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10
    // paddingHorizontal: 15
  },
  monthText: {
    fontSize: 16,
    marginRight: 10,
    fontWeight: 'bold'
  },
  dateContainer: {
    alignItems: 'center',
    marginHorizontal: 10
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'orange',
    marginBottom: 5
  },
  circle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center'
  },
  selectedCircle: {
    backgroundColor: '#f0dfe7' // Light background for the selected day
  },
  dateText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'black'
  },
  selectedText: {
    color: '#a93358' // Darker text for the selected date
  },
  calendarContainer: {
    width: 48,
    height: 'auto',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Maroon']
  },
  topDownCaretIcon: {
    width: 24,
    height: 24
  },
  accordianMainContainer: {
    borderRadius: 12,
    padding: DS.spacing[16],
    overflow: 'hidden'
  },
  subscribeButton: {
    marginTop: 8,
    alignSelf: 'center',
    width: '50%'
  },
  upcomingDateContainer: {
    flex: 9,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around'
  },
  touchableDateCOntainer: {
    borderRadius: 100,
    height: 40,
    width: 40,
    justifyContent: 'center',
    alignItems: 'center'
  },
  selectedDate: {
    height: 8,
    width: 8,
    borderRadius: 4,
    position: 'absolute',
    top: 0,
    backgroundColor: '#FF7222',
    zIndex: 1,
    right: 5
  },
  upcomingEventContainer: {
    marginTop: 12,
    width: '100%',
    padding: DS.spacing[16],
    borderRadius: 8
  },
  calendarAndDetailContainer: {
    flex: 4,
    flexDirection: 'row'
  },
  calendarFrame: {
    width: 48,
    height: 25
  },
  seeAllTimeStatsButton: {
    marginTop: 8,
    alignSelf: 'center'
  },
  practiceGameButton: {
    marginTop: 8,
    alignSelf: 'center'
  },
  turtleFrame: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  downTraingleIcon: {
    alignSelf: 'center',
    marginTop: -2,
    width: 25,
    height: 15
  },
  showdownWinContainer: {
    flex: 2,
    justifyContent: 'center',
    alignItems: 'center'
  },
  versusContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  preflopFoldContainer: {
    flex: 2,
    justifyContent: 'center',
    alignItems: 'center'
  },
  playingStyleContainer: {
    marginTop: 12,
    padding: DS.spacing[16],
    borderRadius: 12
  },
  winRateContainer: {
    flex: 1.5,
    justifyContent: 'center'
  },
  gameEffectivenessContainer: {
    marginTop: 2,
    padding: DS.spacing[16],
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12
  },
  chipsFilledIcon: {
    marginLeft: 10,
    width: 26,
    height: 26
  },
  potWinContainer: {
    width: '49.5%',
    borderTopRightRadius: 12,
    padding: DS.spacing[16]
  },
  recentGameRankContainer: {
    width: '49.5%',
    borderTopLeftRadius: 12,
    padding: DS.spacing[16]
  },
  rankTextContainer: {
    backgroundColor: '#fff',
    borderRadius: 24,
    // paddingVertical: 5,
    justifyContent: 'center',
    alignItems: 'center'
  },
  rankTextGradientContainer: {
    padding: 2,
    borderRadius: 24
  },
  starIcon: {
    width: 25,
    height: 25
  }
})
